"use client";

import { AnimatePresence, motion } from "framer-motion";
import {
  BadgeCheck,
  Briefcase,
  Cpu,
  Gavel,
  Landmark,
  Megaphone,
  ChevronDown,
  ArrowRight,
  Phone,
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { MobileMenu } from "./MobileMenu";


const navLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/blog", label: "Blog" },
  { href: "/contact", label: "Contact" },
  { href: "/careers", label: "Careers" },
];

const megaColumns = [
  {
    title: "Business Consultancy",
    href: "/services",
    icon: Briefcase,
    iconBg: "bg-[#1565C0]/10",
    iconColor: "text-[#1565C0]",
    links: [
      { href: "/services", label: "Strategic Overview" },
      { href: "/about", label: "Growth Advisory" },
      { href: "/360-support", label: "360° Support" },
    ],
  },
  {
    title: "Funding",
    href: "/services/funding",
    icon: Landmark,
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-600",
    links: [
      { href: "/services/funding", label: "Funding Programs" },
      { href: "/services/funding/grants", label: "Grants" },
      { href: "/services/funding/nbfc", label: "NBFC & Loans" },
    ],
  },
  {
    title: "Certification",
    href: "/services/certification",
    icon: BadgeCheck,
    iconBg: "bg-violet-500/10",
    iconColor: "text-violet-600",
    links: [
      { href: "/services/certification", label: "All types ISO certification" },
      { href: "/services/certification", label: "Startup India Certificate" },
      { href: "/services/certification", label: "Udhyam Registeration" },
      { href: "/services/certification", label: "GeM Registeration" },
      { href: "/services/certification", label: "Zed Certification" },
      { href: "/services/certification", label: "Section 80 IAC" },
      { href: "/services/certification", label: "FCRA Registration" },
      { href: "/services/certification", label: "12A & 80G Certification" },
    ],
  },
  {
    title: "Legal",
    href: "/services/legal",
    icon: Gavel,
    iconBg: "bg-amber-500/12",
    iconColor: "text-amber-600",
    links: [
      { href: "/services/legal", label: "Contracts & Policy" },
      { href: "/services/legal", label: "Corporate Governance" },
    ],
  },
  {
    title: "Marketing",
    href: "/services/marketing",
    icon: Megaphone,
    iconBg: "bg-rose-500/10",
    iconColor: "text-rose-600",
    links: [
      { href: "/services/marketing/growthbranding", label: "Brand & Growth" },
      { href: "/services/marketing/performancemarketing", label: "Performance Marketing" },
    ],
  },
  {
    title: "IT Solutions",
    href: "/services/it",
    icon: Cpu,
    iconBg: "bg-cyan-500/10",
    iconColor: "text-cyan-600",
    links: [
      { href: "/services/it/software", label: "Software" },
      { href: "/services/it/appdev", label: "App Dev" },
      { href: "/services/it/cloud", label: "Cloud" },
      { href: "/services/it/webdev", label: "Web Dev" },
      { href: "/services/it/hosting", label: "Hosting" },
      { href: "/services/it/crm", label: "CRM" },
      { href: "/services/it/erp", label: "ERP" },
      { href: "/services/it/aiml", label: "AI/ML" },
      { href: "/services/it/cybersecurity", label: "Cybersecurity" },
      { href: "/services/it/devops", label: "DevOps" },
    ],
  },
];

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <span className="relative block h-[18px] w-6">
      <motion.span
        className="absolute left-0 top-0 block h-[2px] w-full rounded-full bg-current"
        animate={open ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      />
      <motion.span
        className="absolute left-0 top-[8px] block h-[2px] w-full rounded-full bg-current"
        animate={open ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.18 }}
      />
      <motion.span
        className="absolute left-0 top-[16px] block h-[2px] w-full rounded-full bg-current"
        animate={open ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      />
    </span>
  );
}

/* ── Top utility bar (dark navy — brand anchor) ─────────────────────────────── */
function TopBar() {
  return (
    <div className="hidden w-full bg-[#0A1628] lg:block">
      <div className="mx-auto flex max-w-[1280px] items-center justify-between px-6 py-2">
        <p className="text-[10.5px] font-medium tracking-wide text-white/38">
          Empowering Businesses with 360° Solutions — Strategy · Funding · Technology
        </p>
        <div className="flex items-center gap-5">
          <a
            href="tel:+919821605930"
            className="flex items-center gap-1.5 text-[10.5px] font-medium text-white/45 transition hover:text-[#C85D1A]"
          >
            <Phone className="h-3 w-3" />
            +91 98216 05930
          </a>
          <span className="h-3 w-px bg-white/15" />
          <Link
            href="/contact"
            className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#C85D1A] transition hover:text-orange-400"
          >
            Free Consultation →
          </Link>
        </div>
      </div>
    </div>
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaTop, setMegaTop] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateHeaderHeight = useCallback(() => {
    const el = wrapperRef.current;
    if (el) setMegaTop(el.offsetHeight);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useLayoutEffect(() => {
    updateHeaderHeight();
    const el = wrapperRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => updateHeaderHeight());
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateHeaderHeight, scrolled]);

  useEffect(() => {
    if (megaMenuRef.current) {
      megaMenuRef.current.style.setProperty('--mega-top', `${megaTop}px`);
    }
  }, [megaTop]);

  const openMega = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMegaOpen(true);
  };
  const scheduleCloseMega = () => {
    closeTimer.current = setTimeout(() => setMegaOpen(false), 150);
  };
  const cancelCloseMega = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  return (
    <>
      <div
        ref={wrapperRef}
        className="sticky top-0 z-50"
        onMouseLeave={scheduleCloseMega}
      >
        {/* Top bar — collapses on scroll */}
        <AnimatePresence>
          {!scrolled && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <TopBar />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Gradient accent line */}
        {/* <div className="h-[2px] w-full bg-gradient-to-r from-[#1565C0] via-[#C85D1A] to-[#1565C0]" /> */}

        {/* Main header */}
        <header
          ref={headerRef}
          className={cn(
            "w-full border-b transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
            scrolled
              ? "border-black/[0.08] bg-[rgba(250,250,247,0.92)] shadow-[0_4px_24px_rgba(0,0,0,0.09)] backdrop-blur-[14px]"
              : "border-black/[0.07] bg-[#FAFAF7] shadow-[0_1px_0_rgba(0,0,0,0.04)]"
          )}
        >
          <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-6 px-6 py-2 lg:py-2.5">

            {/* Logo */}
            <Link href="/" className="group flex shrink-0 items-center">
              <Image
                src="/images/logo.png"
                alt="FarSight Solutions"
                height={40}
                width={140}
                className="h-12 w-auto object-contain"
                priority
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden flex-1 items-center justify-center gap-0.5 lg:flex" aria-label="Main navigation">
              {navLinks.slice(0, 2).map((l) => (
                <NavLink key={l.href} href={l.href}>{l.label}</NavLink>
              ))}

              {/* Services trigger */}
              <div
                className="relative"
                onMouseEnter={() => { cancelCloseMega(); openMega(); }}
              >
                <Link
                  href="/services"
                  className={cn(
                    "group/svc relative flex items-center gap-1.5 rounded-sm px-4 py-2 text-[13.5px] font-semibold tracking-wide transition-colors duration-150",
                    megaOpen
                      ? "bg-[#EBF2FC] text-[#1565C0]"
                      : "text-[#3A3A3A] hover:bg-[#EDECE7] hover:text-[#1565C0]"
                  )}
                >
                  {megaOpen && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute inset-x-2 -bottom-px h-[2px] rounded-full bg-[#C85D1A]"
                    />
                  )}
                  Services
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 transition-transform duration-200",
                      megaOpen ? "rotate-180 text-[#C85D1A]" : "text-black/30"
                    )}
                  />
                </Link>
              </div>

              {navLinks.slice(2).map((l) => (
                <NavLink key={l.href} href={l.href}>{l.label}</NavLink>
              ))}
            </nav>

            {/* CTA */}
            <div className="hidden shrink-0 items-center gap-3 lg:flex">
              <Link
                href="/contact"
                className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-[#C85D1A] px-5 py-2.5 text-[13px] font-bold text-white shadow-[0_2px_14px_rgba(200,93,26,0.30)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_4px_20px_rgba(200,93,26,0.42)] active:scale-[0.98]"
              >
                <span className="pointer-events-none absolute inset-0 -translate-x-full skew-x-12 bg-white/15 transition-transform duration-700 group-hover:translate-x-full" />
                Get Free Consultation
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-expanded={mobileOpen}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              className="inline-flex h-10 w-10 items-center justify-center rounded-sm border border-black/10 text-[#3A3A3A] transition hover:bg-[#EDECE7] lg:hidden"
            >
              <HamburgerIcon open={mobileOpen} />
            </button>
          </div>
        </header>

        {/* Mega Menu */}
        <AnimatePresence>
          {megaOpen && (
            <motion.div
              key="mega"
              ref={megaMenuRef}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-x-0 z-40 top-[var(--mega-top)]"
              onMouseEnter={() => { cancelCloseMega(); setMegaOpen(true); }}
            >
              <div className="border-t border-black/[0.06] bg-white shadow-[0_12px_40px_rgba(0,0,0,0.09)]">
                <div className="mx-auto max-w-[1280px] px-6 py-10">

                  {/* Section label */}
                  <p className="mb-6 flex items-center gap-2 text-[9.5px] font-bold uppercase tracking-[0.22em] text-[#C85D1A]">
                    <span className="h-px w-5 bg-[#C85D1A]" />
                    Our Services
                    <span className="h-px w-5 bg-[#C85D1A]" />
                  </p>

                  <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-6">
                    {megaColumns.map((col, i) => {
                      const Icon = col.icon;
                      return (
                        <motion.div
                          key={col.title}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.04, duration: 0.2 }}
                          className="min-w-0"
                        >
                          <Link
                            href={col.href}
                            className="group/card mb-3 flex items-center gap-2.5 rounded-lg border border-black/[0.07] bg-[#F8F7F3] p-2.5 transition-all duration-150 hover:border-[#1565C0]/25 hover:bg-[#EBF2FC]"
                          >
                            <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-sm", col.iconBg)}>
                              <Icon className={cn("h-4 w-4", col.iconColor)} strokeWidth={1.75} aria-hidden />
                            </span>
                            <span>
                              <span className="block text-[11.5px] font-semibold leading-tight text-[#1A1A1A]">
                                {col.title}
                              </span>
                              <span className="mt-0.5 flex items-center gap-0.5 text-[9.5px] text-black/30 transition group-hover/card:text-[#C85D1A]">
                                Explore <ArrowRight className="h-2.5 w-2.5" />
                              </span>
                            </span>
                          </Link>

                          <ul className="space-y-1.5 pl-1">
                            {col.links.map((sublink, idx) => (
                              <li key={`${col.title}-${idx}`}>
                                <Link
                                  href={sublink.href}
                                  className="group/link flex items-center gap-1.5 text-[12px] text-black/45 transition-colors duration-150 hover:text-[#1565C0]"
                                >
                                  <span className="h-px w-2.5 shrink-0 rounded-full bg-black/20 transition-all group-hover/link:w-3.5 group-hover/link:bg-[#C85D1A]" />
                                  {sublink.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Bottom CTA strip */}
                  <div className="mt-8 flex items-center justify-between rounded-xl border border-[#1565C0]/15 bg-gradient-to-r from-[#EBF2FC] to-[#FDF2EC] px-6 py-4">
                    <div>
                      <p className="text-sm font-semibold text-[#1A1A1A]">Not sure where to start?</p>
                      <p className="text-xs text-black/45">Our experts will map the right solution for your business</p>
                    </div>
                    <Link
                      href="/contact"
                      className="inline-flex items-center gap-2 rounded-full bg-[#1565C0] px-5 py-2 text-[12px] font-bold text-white transition hover:bg-[#1254A1]"
                    >
                      Talk to an Expert <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}

/* ── Reusable nav link ───────────────────────────────────────────────────────── */
function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="rounded-sm px-4 py-2 text-[13.5px] font-medium tracking-wide text-[#3A3A3A] transition-colors duration-150 hover:bg-[#EDECE7] hover:text-[#1565C0]"
    >
      {children}
    </Link>
  );
}